import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft, Dog as DogIcon, Cake, Weight, HeartPulse, GraduationCap, User, CalendarClock,
  CalendarCheck, ClipboardList, CheckCircle2, RotateCcw, Sparkles, Trophy, Flag, StickyNote,
  Circle, CircleDot, Target, Clock, MapPin,
} from "lucide-react";

const LESSON_ICON = { completed: CheckCircle2, in_progress: CircleDot, not_started: Circle };
const LESSON_CLS = { completed: "text-green-600", in_progress: "text-amber-500", not_started: "text-muted-foreground/50" };
const TL_ICON = {
  joined: Flag, class: CalendarCheck, class_upcoming: CalendarClock,
  homework_assigned: ClipboardList, homework_completed: CheckCircle2, note: StickyNote,
};

export default function ClientDogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/client/dogs/${id}`); setData(r.data); }
    catch (e) { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (hwId, status) => {
    try { await api.patch(`/homework/${hwId}/status`, { status }); toast.success("Updated"); await load(); }
    catch (e) { toast.error("Failed"); }
  };

  if (err) return <div className="text-center py-20 text-muted-foreground">Dog not found.</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const d = data.dog;
  const hw = data.homework || [];
  const hwDone = hw.filter((h) => ["completed", "reviewed"].includes(h.status)).length;
  const hwPct = hw.length ? Math.round((hwDone / hw.length) * 100) : 0;
  const lessons = data.program?.lessons || [];
  const lessonsDone = lessons.filter((l) => l.status === "completed").length;
  const lessonPct = lessons.length ? Math.round((lessonsDone / lessons.length) * 100) : 0;

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/dogs")} data-testid="client-dog-back"><ArrowLeft size={16} /> Back to My Dogs</Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center shrink-0"><DogIcon size={30} className="text-primary" strokeWidth={1.5} /></div>
        <div className="flex-1">
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{d.name}</h1>
          <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-muted-foreground">
            <span>{d.breed || "—"}</span>
            <span className="flex items-center gap-1"><Cake size={14} /> {d.age || "—"}</span>
            <span className="flex items-center gap-1"><Weight size={14} /> {d.weight || "—"}</span>
            <span className="flex items-center gap-1"><HeartPulse size={14} /> {d.gender || "—"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/ai?dog=${d.id}`)} data-testid="dog-ask-ai"><Sparkles size={15} className="text-amber-500" /> Ask HV AI</Button>
          <StatusBadge value={d.current_level} />
          <StatusBadge value={d.progress_status} />
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Snapshot */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><GraduationCap size={13} /> Current Program</div>
              <div className="text-sm font-medium mt-1.5">{data.program?.name || "No active program"}</div>
            </CardContent></Card>
            <Card className="border-border"><CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><User size={13} /> Assigned Trainer</div>
              <div className="text-sm font-medium mt-1.5">{d.trainer_name || "Unassigned"}</div>
            </CardContent></Card>
          </div>

          {/* Training program lessons */}
          {data.program && (
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg flex items-center gap-2"><GraduationCap size={18} strokeWidth={1.5} /> {data.program.name}</CardTitle>
                <span className="text-xs text-muted-foreground">{lessonsDone}/{lessons.length} lessons</span>
              </CardHeader>
              <CardContent>
                <ProgressBar value={lessonPct} className="h-2 mb-4" />
                <ol className="space-y-2">
                  {lessons.map((l, i) => {
                    const Icon = LESSON_ICON[l.status] || Circle;
                    return (
                      <li key={l.id || i} className="flex items-start gap-3" data-testid="client-lesson-item">
                        <Icon size={18} className={LESSON_CLS[l.status]} strokeWidth={1.75} />
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${l.status === "not_started" ? "text-muted-foreground" : ""}`}>{l.title}</div>
                          {l.description && <div className="text-xs text-muted-foreground">{l.description}</div>}
                        </div>
                        <StatusBadge value={l.status} />
                      </li>
                    );
                  })}
                  {lessons.length === 0 && <p className="text-sm text-muted-foreground">No lessons in this program.</p>}
                </ol>
              </CardContent>
            </Card>
          )}

          {/* Homework */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2"><ClipboardList size={18} strokeWidth={1.5} /> Homework</CardTitle>
              <span className="text-xs text-muted-foreground">{hwDone}/{hw.length} done</span>
            </CardHeader>
            <CardContent className="space-y-2">
              {hw.length ? hw.map((h) => (
                <div key={h.id} className="p-3 rounded-lg border border-border/60" data-testid="client-detail-homework">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2"><span className="text-sm font-medium">{h.title}</span><StatusBadge value={h.status} /></div>
                      {h.instructions && <p className="text-xs text-muted-foreground mt-1">{h.instructions}</p>}
                      <div className="text-xs text-muted-foreground mt-1">Due {fmtDate(h.due_date)}{h.video_url ? " · has video" : ""}</div>
                    </div>
                    {h.status === "assigned" ? (
                      <Button size="sm" className="gap-1.5" onClick={() => setStatus(h.id, "completed")} data-testid="client-hw-complete"><CheckCircle2 size={15} /> Mark done</Button>
                    ) : h.status === "completed" ? (
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setStatus(h.id, "assigned")} data-testid="client-hw-undo"><RotateCcw size={15} /> Undo</Button>
                    ) : null}
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No homework assigned yet.</p>}
            </CardContent>
          </Card>

          {/* Classes */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><CalendarClock size={16} strokeWidth={1.5} /> Upcoming Classes</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.upcoming_classes?.length ? data.upcoming_classes.map((c) => (
                  <div key={c.class_id} className="p-2.5 rounded-lg border border-border/60 cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/classes/${c.class_id}`)} data-testid="client-detail-upcoming">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5"><Clock size={11} /> {fmtDate(c.date)} · {c.time}</div>
                  </div>
                )) : <p className="text-sm text-muted-foreground">None scheduled.</p>}
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><CalendarCheck size={16} strokeWidth={1.5} /> Completed Classes</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.completed_classes?.length ? data.completed_classes.map((c) => (
                  <div key={c.class_id} className="p-2.5 rounded-lg border border-border/60 flex items-center justify-between" data-testid="client-detail-completed">
                    <div><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{fmtDate(c.date)}</div></div>
                    <StatusBadge value={c.attendance_status} />
                  </div>
                )) : <p className="text-sm text-muted-foreground">None yet.</p>}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sidebar: progress, next step, timeline */}
        <div className="space-y-6">
          <Card className="border-border bg-secondary/40">
            <CardContent className="p-5">
              <div className="flex items-center justify-between text-sm mb-1.5"><span className="text-muted-foreground">Homework completion</span><span className="font-medium">{hwPct}%</span></div>
              <ProgressBar value={hwPct} className="h-2" />
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><Target size={13} /> Training Goals</div>
                <p className="text-sm mt-1">{d.training_goals || "—"}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border border-dashed">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-primary flex items-center gap-1.5"><Sparkles size={13} /> Recommended Next Step</div>
              <p className="text-sm font-medium mt-1.5">{data.recommended_next_step}</p>
            </CardContent>
          </Card>

          <Card className="border-border border-dashed">
            <CardContent className="p-5">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><Trophy size={13} /> Achievements</div>
              <p className="text-sm text-muted-foreground mt-1.5">Badges & certificates arrive with HV Academy — coming soon.</p>
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg">Progress Timeline</CardTitle></CardHeader>
            <CardContent>
              <div className="relative pl-6">
                <div className="absolute left-[9px] top-1 bottom-1 w-px bg-border" />
                <div className="space-y-4">
                  {(data.timeline || []).map((t, i) => {
                    const Icon = TL_ICON[t.type] || StickyNote;
                    return (
                      <div key={i} className="relative" data-testid="client-timeline-item">
                        <div className="absolute -left-6 top-0.5 h-[18px] w-[18px] rounded-full bg-card border border-border flex items-center justify-center"><Icon size={11} className="text-primary" /></div>
                        <div className="text-sm font-medium leading-tight">{t.label}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(t.date)}{t.detail ? ` · ${t.detail}` : ""}</div>
                        {t.recommended_next_step && <div className="text-xs text-primary mt-0.5">→ {t.recommended_next_step}</div>}
                      </div>
                    );
                  })}
                  {(!data.timeline || data.timeline.length === 0) && <p className="text-sm text-muted-foreground">No activity yet.</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
